import { useState, useEffect, useCallback } from 'react';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3010';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function usePushNotifications() {
  const [permission, setPermission] = useState(Notification.permission);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);

  const isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  const isSecureContext =
    typeof window !== 'undefined' &&
    (window.location.protocol === 'https:' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');
  const isConfigured = Boolean(VAPID_PUBLIC_KEY) && Boolean(BACKEND_URL);

  useEffect(() => {
    if (!isSupported) return;
    if (!isSecureContext) return;
    navigator.serviceWorker.register('/sw.js').then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        if (sub) setSubscription(sub);
      });
    });
  }, [isSupported, isSecureContext]);

  const subscribe = useCallback(async () => {
    if (!isSupported) return { ok: false, reason: 'not_supported' };
    if (!isSecureContext) return { ok: false, reason: 'not_secure_context' };
    if (!VAPID_PUBLIC_KEY) {
      setError('REACT_APP_VAPID_PUBLIC_KEY no está definida');
      return { ok: false, reason: 'missing_vapid_public_key' };
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return { ok: false, reason: 'denied' };

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const resp = await fetch(`${BACKEND_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sub),
      });

      if (!resp.ok) {
        const payload = await resp.json().catch(() => null);
        throw new Error(payload?.error || `Subscribe failed: ${resp.status}`);
      }

      setSubscription(sub);
      return { ok: true };
    } catch (err) {
      setError(err?.message || String(err));
      return { ok: false, reason: err?.message || String(err) };
    }
  }, [isSupported, isSecureContext]);

  const unsubscribe = useCallback(async () => {
    if (!subscription) return;
    await subscription.unsubscribe();
    await fetch(`${BACKEND_URL}/api/push/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    });
    setSubscription(null);
  }, [subscription]);

  return { isSupported, isSecureContext, isConfigured, permission, subscription, subscribe, unsubscribe, error };
}
